import { useCallback, useState } from 'react';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ActionIcon,
  Box,
  Collapse,
  Group,
  Paper,
  Switch,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconGripVertical, IconX } from '@tabler/icons-react';
import TextInput from 'src/components/design-system/input/TextInput';
import { FieldConfig, SectionConfig } from '../types';
import FieldList from './FieldList';

type SectionItemProps = {
  section: SectionConfig;
  onToggleVisible: () => void;
  onDisplayNameChange: (displayName: string | null) => void;
  onFieldsChange: (fields: FieldConfig[]) => void;
};

export default function SectionItem({
  section,
  onToggleVisible,
  onDisplayNameChange,
  onFieldsChange,
}: SectionItemProps) {
  const [expanded, setExpanded] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.tableTag,
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleFieldDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = section.fields.findIndex(f => f.entityFieldName === active.id);
      const newIndex = section.fields.findIndex(f => f.entityFieldName === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      onFieldsChange(arrayMove(section.fields, oldIndex, newIndex));
    },
    [section.fields, onFieldsChange],
  );

  const handleToggleFieldVisible = useCallback(
    (fieldIndex: number) => {
      const updated = section.fields.map((f, i) =>
        i === fieldIndex ? { ...f, visible: !f.visible } : f,
      );
      onFieldsChange(updated);
    },
    [section.fields, onFieldsChange],
  );

  const handleFieldLabelChange = useCallback(
    (fieldIndex: number, label: string | null) => {
      const updated = section.fields.map((f, i) => (i === fieldIndex ? { ...f, label } : f));
      onFieldsChange(updated);
    },
    [section.fields, onFieldsChange],
  );

  const visibleFieldCount = section.fields.filter(f => f.visible).length;
  const hasCustomDisplayName = section.displayName !== null && section.displayName !== '';

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      withBorder
      radius="sm"
      opacity={section.visible ? 1 : 0.5}
    >
      <Group gap="sm" px="md" py="sm" wrap="nowrap">
        <IconGripVertical
          size={18}
          style={{ cursor: 'grab', flexShrink: 0, color: 'var(--mantine-color-gray-5)' }}
          {...attributes}
          {...listeners}
        />
        <UnstyledButton onClick={() => setExpanded(!expanded)} style={{ flexShrink: 0 }}>
          {expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
        </UnstyledButton>
        <Box style={{ flex: 1 }}>
          <TextInput
            placeholder={section.defaultDisplayName}
            value={section.displayName ?? ''}
            onChange={e => onDisplayNameChange(e.currentTarget.value || null)}
          />
        </Box>
        {hasCustomDisplayName && (
          <ActionIcon
            variant="subtle"
            size="xs"
            color="gray"
            onClick={() => onDisplayNameChange(null)}
          >
            <IconX size={12} />
          </ActionIcon>
        )}
        <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
          {visibleFieldCount}/{section.fields.length} fields
        </Text>
        <Switch
          size="sm"
          checked={section.visible}
          onChange={onToggleVisible}
          label={section.visible ? 'Visible' : 'Hidden'}
          styles={{
            label: {
              fontSize: 'var(--mantine-font-size-xs)',
              color: section.visible ? undefined : 'var(--mantine-color-dimmed)',
            },
          }}
        />
      </Group>
      <Collapse in={expanded}>
        <Box pb="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleFieldDragEnd}
          >
            <FieldList
              fields={section.fields}
              onToggleFieldVisible={handleToggleFieldVisible}
              onFieldLabelChange={handleFieldLabelChange}
            />
          </DndContext>
        </Box>
      </Collapse>
    </Paper>
  );
}
